import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { When } from 'react-if';
import { useSelector } from 'react-redux';
import {
    Card,
    Table,
    TableBody,
    TableCell,
    Avatar,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
    Stack,
    TableSortLabel,
    Grid2,
    Button,
    Tooltip,
    Box,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import Loading from '../../loading';
import { RootState } from '../../../store';
import { PaginationFactory, PaginatorFactory } from '../../../factory';
import { UserService } from '../../../services/user.service';
import { USER_COLUMNS, LAST_PING_COL_FIELD } from '../../../builders/users-builders';
import NoData from '../../no-data';
import { CustomerTypes, UserTypes } from '../../../types';
import AreYouSureModal from '../../are-you-sure-modal';
import { CustomerService } from '../../../services/customer.service';
import { UserDetailsDrawer } from '../../details-drawers/user-details-drawer';
import { permissionCheck } from '../../../helpers/permissionCheck';
import { customerPermissionFlags } from '../../../constants/systemPermissions.constants';
import { UserListFilters } from './users-list-filters';
import { UsersSecondaryFilterRow, TriState } from './secondary-filter-row';
import { DEFAULT_APP_FILTER, triToShowActiveUsers } from './app-filter';
import { APP_STATUS_META, deriveAppStatus } from './app-status';
import { LAST_PING_SOURCE_LABEL, deriveLastPingSource, deriveLastPingTime } from './last-ping-source';
import { downloadCsv } from '../../../hooks/useCsvDownload';
import CloudUpload from '@mui/icons-material/CloudUpload';
import { ImportUsersModal } from '../../import-users-modal';
import { ImportResultsModal } from '../../import-results-modal';
import { SystemConstantsService } from '../../../services/system-constants.service';
import { ComponentPermissionGateway } from '../../component-permission-gateway';
import { AddNewUsersModal } from '../../add-new-user-modal';
import { useGenericQuery } from '../../../services/react-query/generic.query';
import { useEffectiveSettings, SETTING } from '../../../hooks/use-effective-settings';
import { Team } from '../../../types/teams.types';
import { Group } from '../../../types/groups.types';
import { TagsService } from '../../../services/tags-service';
import { DashboardApiService } from '../../../services/dashboard.service';
import type { TagOption } from '../../../pages/app/reports/groups-teams-activity/types';
import { GT_COLORS } from '../../../constants/designTokens.constants';

dayjs.extend(relativeTime);

const DARK_BLUE = '#206FA5';

// Device icon lifted from the design handoff (POM Users.zip → src/icons.jsx
// Device entry). Represents a paired wearable POM device.
const DeviceIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="6" y="4" width="12" height="16" rx="3" />
        <circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none" />
        <path d="M10 18h4" />
    </svg>
);

const getInitials = (user: UserTypes.Model): string => {
    const f = (user?.firstName ?? '').trim();
    const l = (user?.lastName ?? '').trim();
    return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || '?';
};

const formatRelativePing = (raw: Date | string | null | undefined): string => {
    if (!raw) return 'N/A';
    const d = dayjs(raw);
    if (!d.isValid() || d.year() < 2000) return 'N/A';
    const ageDays = dayjs().diff(d, 'day');
    return ageDays > 7 ? d.format('MMM DD, YYYY') : d.fromNow();
};

// Keep families together when sorting by name — same trick the design spec
// calls for ("compares firstName + lastName").
function compareVersions(a = '', b = ''): number {
    const pa = a.split('.').map(Number);
    const pb = b.split('.').map(Number);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
        const na = pa[i] || 0;
        const nb = pb[i] || 0;
        if (na > nb) return 1;
        if (na < nb) return -1;
    }
    return 0;
}

export const UserList: React.FC = () => {
    const [importUsersModal, setImportUsersModal] = useState<boolean>(false);
    const [importResults, setImportResults] = useState<UserTypes.ImportExecuteResponse | null>(null);
    const [addUserModal, setAddUserModal] = useState<boolean>(false);
    const [toggleRefresh, setToggleRefresh] = useState(false);
    // Mobile App defaults to All (see app-filter.ts for why); admins narrow
    // to Yes/No from the segmented control.
    const [appFilter, setAppFilter] = useState<TriState>(DEFAULT_APP_FILTER);
    const [deviceFilter, setDeviceFilter] = useState<TriState>('all');

    // Roving-tabindex: only one row at a time is in the tab order so admins
    // can step into the table once and navigate with the arrow keys without
    // a Tab stop per row.
    const rowRefs = useRef<Array<HTMLTableRowElement | null>>([]);
    const [focusedRowIndex, setFocusedRowIndex] = useState(0);

    const {
        pagination: { total, items },
        loading,
    } = useSelector((state: RootState) => state.user);
    const { loading: customerLoading, minAppVersion } = useSelector(
        (state: RootState) => state.customer
    );
    const allTags = useSelector((state: RootState) => state.tags.items);

    // Only show tags that are actually assigned to at least one user.
    const [userTagNames, setUserTagNames] = useState<Set<string> | null>(null);
    useEffect(() => {
        let cancelled = false;
        DashboardApiService.fetchTagOptions('user')
            .then((opts: TagOption[]) => {
                if (!cancelled) setUserTagNames(new Set(opts.map((o) => o.name)));
            })
            .catch((err) => {
                console.error('[UserList] Failed to load user tag options:', err);
                if (!cancelled) setUserTagNames(new Set());
            });
        return () => {
            cancelled = true;
        };
    }, []);
    const userAssignedTags = useMemo(
        () => (userTagNames ? allTags.filter((t) => userTagNames.has(t.name)) : []),
        [allTags, userTagNames],
    );

    const [selectedUser, setSelectedUser] = useState({} as UserTypes.Model);
    const [modal, setModal] = useState({
        removeFromSubscription: false,
        details: false,
    });

    const [sort, setSort] = useState<PaginatorFactory<any>['sort']>({ firstName: 'asc' });
    const [pagination, setPagination] = useState<PaginatorFactory<any>>({ page: 0, perPage: 25 });

    const [searchParams] = useSearchParams();
    const [filter, setFilter] = useState<UserTypes.Filters>(() => {
        const VALID_ACTIVITY: UserTypes.ActivityWindow[] = [
            'today', '7days', '30days',
            'inactive-today', 'inactive-7days', 'inactive-30days',
        ];
        const urlActivity = searchParams.get('activity') || '';
        const activity = (VALID_ACTIVITY as string[]).includes(urlActivity)
            ? (urlActivity as UserTypes.ActivityWindow)
            : '';
        return {
            name: '', phoneNumber: '', employeeId: '',
            activity: activity as UserTypes.Filters['activity'],
        };
    });

    const toggleModal = (k: keyof typeof modal, v: boolean) =>
        setModal((m) => ({ ...m, [k]: v }));

    // Compose the filter object sent to the API: spread the user-applied
    // filters and tack on the segmented Device toggle. App filter goes to
    // the showActiveUsers param, not into filter.
    const composeFilter = useCallback(
        (base: UserTypes.Filters, device: TriState): UserTypes.Filters => ({
            ...base,
            ...(device === 'yes' || device === 'no' ? { hasDevice: device } : {}),
        }),
        [],
    );

    const onFilter = async (filters: UserTypes.Filters) => {
        // Apply always restarts pagination — whether the user added,
        // changed, or cleared filters, the resulting result set is fresh
        // and there's no meaningful "page 4" to preserve. Matches the
        // segmented Device/App handlers, which also reset to page 0.
        setFilter(filters);
        setPagination((p) => ({ ...p, page: 0 }));

        await UserService.fetchMany(
            {
                ...pagination,
                page: 1,
                filter: composeFilter(filters, deviceFilter),
                sort,
            },
            undefined,
            false,
            triToShowActiveUsers(appFilter),
        );
    };

    const makeCsvDownload = async () => {
        const res: PaginationFactory<UserTypes.Model> = (await UserService.fetchMany(
            { perPage: 100000, page: 1, filter: composeFilter(filter, deviceFilter), sort },
            undefined,
            true,
            triToShowActiveUsers(appFilter),
        )) as PaginationFactory<UserTypes.Model>;

        // tagId → name lookup for resolving each user's tag IDs into
        // human-readable names. Built once per export rather than per row.
        const tagNameById = new Map<string, string>();
        for (const t of allTags) {
            if (t._id) tagNameById.set(t._id, t.name);
        }

        // Resolve each user's tag IDs to names and track the widest tag
        // list so every row's CSV shape matches (downloadCsv derives the
        // header row from the keys of items[0], so missing keys on later
        // rows would silently shift columns out of alignment).
        const userTagLists: string[][] = (res.items ?? []).map((user) =>
            (user?.tags ?? [])
                .map((tid) => tagNameById.get(tid))
                .filter((n): n is string => !!n)
        );
        const maxTags = userTagLists.reduce((m, list) => Math.max(m, list.length), 0);
        // Match the bulk-upload column convention: tagOne, tagTwo, ... so
        // a round-trip export → edit → import maps tags back cleanly. Past
        // ten, fall back to numeric (tag11, tag12, …) — the import parser
        // accepts anything starting with "tag" (import-users-modal extracts
        // tags from every column whose key startsWith('tag')).
        const TAG_WORDS = ['One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
        const tagColKey = (i: number) => `tag${TAG_WORDS[i] ?? i + 1}`;

        const csvArray = res.items?.map((user, idx) => {
            const status = deriveAppStatus(user);
            const meta = APP_STATUS_META[status];
            const tagCols: Record<string, string> = {};
            for (let i = 0; i < maxTags; i++) {
                tagCols[tagColKey(i)] = userTagLists[idx][i] ?? '';
            }
            // Resolve team name once per row — getTeamName scans allTeams,
            // so calling it twice doubled the work per CSV row.
            const teamNameResolved = getTeamName(user);
            // Skip the derivation entirely when the column is hidden — the
            // export path fetches up to 100k rows.
            const lastPingTime = hideLastPing ? null : deriveLastPingTime(user);
            return {
                userName: `${user?.firstName ?? '[No Name]'} ${user?.lastName ?? '[No Name]'}`,
                groupName: user?.group?.name ?? '',
                teamName: teamNameResolved === 'N/A' ? '' : teamNameResolved,
                phoneNumber: user?.phoneNumber ?? '',
                backupEmail: user?.backupEmail ?? '',
                employeeId: user?.employeeId ?? '',
                mobileApp: meta.mobileApp,
                appStatusLabel: meta.label,
                ...(!hideLastPing && {
                    lastAppPing: lastPingTime ? dayjs(lastPingTime).format('LLL') : 'N/A',
                }),
                appVersion: user?.appVersion ?? 'N/A',
                createdAt: user?.createdAt ? dayjs(user.createdAt).format('LLL') : 'N/A',
                // Tags trail at the end so adding a new column later
                // doesn't shift tagOne/tagTwo/... and break any external
                // integration parsing this CSV.
                ...tagCols,
            };
        });

        downloadCsv(csvArray, 'pom-users');
    };

    // Refetch on pagination / sort / segmented-filter changes. Filter object
    // changes route through onFilter (Apply button) so we don't include it
    // in this dep array.
    useEffect(() => {
        UserService.fetchMany(
            {
                ...pagination,
                page: pagination.page + 1,
                filter: composeFilter(filter, deviceFilter),
                sort,
            },
            undefined,
            false,
            triToShowActiveUsers(appFilter),
        );
        // Why: `filter` is intentionally excluded — applying text/team/tag
        // filters runs through the Apply button (`onFilter`), not this
        // effect. Including it would refetch on every keystroke.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination?.perPage, pagination?.page, sort, appFilter, deviceFilter, toggleRefresh]);

    useEffect(() => {
        SystemConstantsService.getMinAppVersion();
    }, []);

    useEffect(() => {
        TagsService.fetchAll(() => undefined);
    }, []);

    const { data: allTeams, isLoading: isTeamsLoading } = useGenericQuery<Team[]>(
        'fetchTeams',
        'teams/fetchTeams',
        0,
        0,
        false,
    );

    // Groups feed the new Group filter and let the Team filter render
    // teams sectioned by their parent group (disambiguates customers like
    // Molina that have duplicate team names across multiple groups). The
    // existing groups endpoint is intentionally heavy (groups + teams +
    // users) — fine for the rare filter-dropdown population; switch to a
    // lighter endpoint if this becomes a bottleneck.
    const { data: allGroups } = useGenericQuery<Group[]>(
        'fetchGroups',
        'groups/fetch-all-groups-teams-users',
        0,
        0,
        false,
    );

    const { getSetting, isLoading: isCustomerSettingsLoading, isSuccess: isCustomerSettingsLoaded } = useEffectiveSettings();
    const hideLastPing = getSetting(SETTING.hideLastPing, false);
    const userUniqueIdentifier = getSetting(SETTING.userUniqueIdentifier, '');
    // Preserve the prop shape the child modals/drawers expect; only
    // userUniqueIdentifier is read off it downstream (via use-user-formik).
    // Only present once the effective settings have actually loaded, mirroring
    // the old react-query `data` (undefined until success). Downstream gates
    // like `!!customerSettings && !isLoading` then stay accurate on a fetch
    // error instead of treating a synthetic object as "loaded".
    const customerSettings = isCustomerSettingsLoaded
        ? ({ userUniqueIdentifier } as CustomerTypes.OnboardingForm)
        : undefined;

    const getTeamName = (user: UserTypes.Model): string => {
        // Pending users sometimes serialize teamId as { $oid: '...' }; unwrap
        // unconditionally so the lookup works for both populations.
        const raw: any = (user as any)?.teamId;
        const teamId = raw?.$oid ?? raw;
        if (!teamId) return 'N/A';
        return allTeams?.find((t) => (t as any)?.id === teamId)?.name ?? 'N/A';
    };

    const canRemove = permissionCheck(customerPermissionFlags.TEAM_LEADER);

    const openUserDetails = (user: UserTypes.Model) => {
        // Don't open the drawer when the user is actually trying to copy
        // text (drag-select inside a cell). If there's a non-collapsed
        // selection on click, treat it as a copy gesture, not a row click.
        const sel = window.getSelection();
        if (sel && !sel.isCollapsed && sel.toString().length > 0) return;

        setSelectedUser(user);
        toggleModal('details', true);
    };

    // App-version is sorted client-side (semver-style), per legacy behavior.
    const sortedItems = sort && sort['appVersion']
        ? [...(items || [])].sort((a, b) => {
            const direction = sort['appVersion'] === 'asc' ? 1 : -1;
            return compareVersions(a.appVersion ?? '', b.appVersion ?? '') * direction;
        })
        : items;

    // Clamp the roving-tabindex pointer when the row count shrinks (filter
    // or page change). Functional setState reads the current value so we
    // don't need to dep on focusedRowIndex (avoids resubscribing every
    // time focus moves). Must run on every render — declared above the
    // isCustomerSettingsLoading early-return so hook order stays stable.
    const sortedLen = sortedItems?.length ?? 0;
    useEffect(() => {
        setFocusedRowIndex((prev) => (prev >= sortedLen && sortedLen > 0 ? 0 : prev));
    }, [sortedLen]);

    if (isCustomerSettingsLoading) {
        return <Loading />;
    }

    const visibleColumns = USER_COLUMNS.filter(
        (col) => !(hideLastPing && col.field === LAST_PING_COL_FIELD),
    );

    return (
        <>
            <Grid2
                container
                display="flex"
                flexDirection="row"
                justifyContent="flex-end"
                alignItems="center"
                sx={{ mb: 2 }}
            >
                <ComponentPermissionGateway role={customerPermissionFlags.GROUP_LEADER}>
                    <Grid2 container spacing={1}>
                        <Button
                            variant="contained"
                            onClick={() => setAddUserModal(true)}
                            sx={{
                                backgroundColor: GT_COLORS.primary,
                                color: '#ffffff',
                                textTransform: 'none',
                                p: '5px 16px',
                                '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                            }}
                        >
                            Add New User
                        </Button>
                        <Button
                            variant="outlined"
                            endIcon={<CloudUpload />}
                            onClick={() => setImportUsersModal(true)}
                            sx={{
                                color: GT_COLORS.primary,
                                borderColor: GT_COLORS.primary,
                                textTransform: 'none',
                                '&:hover': {
                                    borderColor: GT_COLORS.primaryHover,
                                    backgroundColor: `${GT_COLORS.primary}08`,
                                },
                            }}
                        >
                            Import Users
                        </Button>
                    </Grid2>
                </ComponentPermissionGateway>
            </Grid2>

            <Card sx={{ mt: 2 }}>
                <UserListFilters
                    onFilter={onFilter}
                    onDownload={makeCsvDownload}
                    allTeams={allTeams || []}
                    allGroups={allGroups || []}
                    allTags={userAssignedTags}
                />
                <UsersSecondaryFilterRow
                    appFilter={appFilter}
                    deviceFilter={deviceFilter}
                    onAppFilterChange={(v) => {
                        setAppFilter(v);
                        setPagination((p) => ({ ...p, page: 0 }));
                    }}
                    onDeviceFilterChange={(v) => {
                        setDeviceFilter(v);
                        setPagination((p) => ({ ...p, page: 0 }));
                    }}
                    count={total ?? 0}
                />

                <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
                    <Table sx={{ minWidth: 1040 }}>
                        <TableHead>
                            <TableRow>
                                {visibleColumns.map((column) => (
                                    <TableCell
                                        key={column.headerName || `col-${column.width}`}
                                        sortDirection={
                                            (column.field && sort![column.field]) || undefined
                                        }
                                    >
                                        {/* Columns explicitly opt out of sorting by setting
                                            sortable:false (e.g. derived/joined fields like Group
                                            that aren't server-sortable). Default is sortable. */}
                                        {column.field && (column as any).sortable !== false ? (
                                            <TableSortLabel
                                                active={!!sort![column.field]}
                                                direction={sort![column.field]}
                                                onClick={() => {
                                                    setSort((s) => ({
                                                        [column.field]:
                                                            s![column.field] === 'asc' ? 'desc' : 'asc',
                                                    }));
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: 'text.primary' }}
                                                >
                                                    {column.headerName}
                                                </Typography>
                                            </TableSortLabel>
                                        ) : (
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'text.primary' }}
                                            >
                                                {column.headerName}
                                            </Typography>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <When
                            condition={
                                !loading?.fetchMany &&
                                !isTeamsLoading &&
                                !isCustomerSettingsLoading
                            }
                        >
                            <TableBody>
                                {sortedItems?.map((user, index) => {
                                    const status = deriveAppStatus(user);
                                    const meta = APP_STATUS_META[status];
                                    const device = user.devices?.[0];
                                    const pingSource = deriveLastPingSource(user);
                                    const lastPingTime = deriveLastPingTime(user);
                                    const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
                                    return (
                                        <TableRow
                                            key={user?.id}
                                            ref={(el) => {
                                                rowRefs.current[index] = el;
                                            }}
                                            tabIndex={index === focusedRowIndex ? 0 : -1}
                                            aria-label={
                                                fullName
                                                    ? `View details for ${fullName}`
                                                    : 'View user details'
                                            }
                                            onClick={() => openUserDetails(user)}
                                            onFocus={() => setFocusedRowIndex(index)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    openUserDetails(user);
                                                    return;
                                                }
                                                const last = (sortedItems?.length ?? 0) - 1;
                                                if (e.key === 'ArrowDown') {
                                                    e.preventDefault();
                                                    const next = Math.min(index + 1, last);
                                                    setFocusedRowIndex(next);
                                                    rowRefs.current[next]?.focus();
                                                } else if (e.key === 'ArrowUp') {
                                                    e.preventDefault();
                                                    const prev = Math.max(index - 1, 0);
                                                    setFocusedRowIndex(prev);
                                                    rowRefs.current[prev]?.focus();
                                                } else if (e.key === 'Home') {
                                                    e.preventDefault();
                                                    setFocusedRowIndex(0);
                                                    rowRefs.current[0]?.focus();
                                                } else if (e.key === 'End') {
                                                    e.preventDefault();
                                                    setFocusedRowIndex(last);
                                                    rowRefs.current[last]?.focus();
                                                }
                                            }}
                                            sx={{
                                                ':hover': {
                                                    backgroundColor: '#F0F0F0',
                                                    cursor: 'pointer',
                                                },
                                                ':focus-visible': {
                                                    outline: '2px solid',
                                                    outlineColor: 'primary.main',
                                                    outlineOffset: -2,
                                                },
                                            }}
                                        >
                                            <TableCell>
                                                <Stack
                                                    spacing={1.5}
                                                    direction="row"
                                                    alignItems="center"
                                                >
                                                    <Avatar
                                                        alt={user?.firstName || '[No Name]'}
                                                        src={user?.profilePhoto || undefined}
                                                        sx={{
                                                            width: 36,
                                                            height: 36,
                                                            bgcolor: DARK_BLUE,
                                                            color: '#fff',
                                                            fontSize: 13,
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {getInitials(user)}
                                                    </Avatar>
                                                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                                                            <Typography
                                                                fontSize={14}
                                                                fontWeight={600}
                                                                noWrap
                                                            >
                                                                {user?.firstName || '[No Name]'}{' '}
                                                                {user?.lastName || '[No Name]'}
                                                            </Typography>
                                                            {/* Soft-deleted users only appear here when an admin searches
                                                                by identifier (default list hides them). The chip makes
                                                                that state unmistakable so it's not confused with an
                                                                active user with stale data. */}
                                                            {user?.deletedAt && (
                                                                <Box
                                                                    sx={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        px: 0.75,
                                                                        height: 18,
                                                                        borderRadius: '9px',
                                                                        bgcolor: '#FEE2E2',
                                                                        color: '#991B1B',
                                                                        fontSize: 10,
                                                                        fontWeight: 700,
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '.3px',
                                                                        flexShrink: 0,
                                                                    }}
                                                                >
                                                                    Deleted
                                                                </Box>
                                                            )}
                                                        </Stack>
                                                        {user?.employeeId && (
                                                            <Tooltip title="Employee ID">
                                                                <Box
                                                                    sx={{
                                                                        display: 'inline-flex',
                                                                        alignSelf: 'flex-start',
                                                                        px: 0.75,
                                                                        height: 20,
                                                                        alignItems: 'center',
                                                                        borderRadius: '6px',
                                                                        bgcolor: 'grey.200',
                                                                        color: 'grey.700',
                                                                        fontFamily:
                                                                            'JetBrains Mono, monospace',
                                                                        fontSize: 11,
                                                                        fontWeight: 600,
                                                                        cursor: 'help',
                                                                    }}
                                                                >
                                                                    {user.employeeId}
                                                                </Box>
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <span>{user?.group?.name ?? 'N/A'}</span>
                                            </TableCell>
                                            <TableCell>
                                                <span>{getTeamName(user)}</span>
                                            </TableCell>
                                            <TableCell>
                                                <span>{user?.phoneNumber ?? 'N/A'}</span>
                                            </TableCell>
                                            <TableCell>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            bgcolor: meta.color,
                                                            boxShadow: `0 0 0 3px ${meta.ring}`,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            fontSize: 13,
                                                            fontWeight: 600,
                                                            color: 'grey.800',
                                                        }}
                                                    >
                                                        {meta.mobileApp}
                                                    </Typography>
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            fontSize: 11,
                                                            color: 'grey.600',
                                                        }}
                                                    >
                                                        · {meta.label}
                                                    </Typography>
                                                    {device && (
                                                        <Tooltip
                                                            title={
                                                                <Box
                                                                    sx={{
                                                                        fontSize: 11,
                                                                        lineHeight: 1.4,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            fontSize: 9,
                                                                            fontWeight: 700,
                                                                            textTransform:
                                                                                'uppercase',
                                                                            letterSpacing: '1px',
                                                                            opacity: 0.7,
                                                                        }}
                                                                    >
                                                                        Paired Device
                                                                    </Box>
                                                                    <Box
                                                                        sx={{
                                                                            fontFamily:
                                                                                'JetBrains Mono, monospace',
                                                                            fontWeight: 700,
                                                                        }}
                                                                    >
                                                                        {device.serialNumber ?? '—'}
                                                                    </Box>
                                                                    {(device as any)?.createdAt && (
                                                                        <Box>
                                                                            Paired{' '}
                                                                            {dayjs(
                                                                                (device as any)
                                                                                    ?.createdAt,
                                                                            ).format(
                                                                                'MMM DD, YYYY',
                                                                            )}
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            }
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: 22,
                                                                    height: 22,
                                                                    borderRadius: '6px',
                                                                    bgcolor:
                                                                        'rgba(32,111,165,0.1)',
                                                                    border: '1px solid rgba(32,111,165,0.24)',
                                                                    color: DARK_BLUE,
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    ml: 0.5,
                                                                }}
                                                            >
                                                                <DeviceIcon />
                                                            </Box>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            {!hideLastPing && (
                                                <TableCell>
                                                    <Tooltip
                                                        title={
                                                            lastPingTime ? (
                                                                <Box
                                                                    sx={{
                                                                        fontSize: 11,
                                                                        lineHeight: 1.4,
                                                                    }}
                                                                >
                                                                    <Box>
                                                                        {dayjs(
                                                                            lastPingTime,
                                                                        ).format('LLL')}
                                                                    </Box>
                                                                    <Box sx={{ opacity: 0.8 }}>
                                                                        {pingSource
                                                                            ? `Received from ${LAST_PING_SOURCE_LABEL[pingSource]}`
                                                                            : 'Ping source unknown'}
                                                                    </Box>
                                                                </Box>
                                                            ) : (
                                                                'No ping recorded'
                                                            )
                                                        }
                                                    >
                                                        <span>
                                                            {formatRelativePing(lastPingTime)}
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <Tooltip
                                                    title={`Suggested minimum app version: ${minAppVersion ?? 'Loading...'}`}
                                                >
                                                    <span>{user?.appVersion ?? 'N/A'}</span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </When>
                    </Table>

                    <When condition={loading.fetchMany === false && items?.length < 1}>
                        <NoData
                            buttonText={null}
                            onClick={() => undefined}
                            title="No records found"
                            description="There are no users registered in your subscription."
                        />
                    </When>

                    <When condition={loading?.fetchMany}>
                        <Loading />
                    </When>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={total ?? 0}
                    rowsPerPage={pagination?.perPage}
                    page={pagination?.page}
                    onPageChange={(_, p) => setPagination((pag) => ({ ...pag, page: p }))}
                    onRowsPerPageChange={(e) =>
                        setPagination((pag) => ({
                            ...pag,
                            perPage: parseInt(e.target.value, 10),
                        }))
                    }
                />
            </Card>

            {importUsersModal && (
                <ImportUsersModal
                    visible={importUsersModal}
                    onClose={() => setImportUsersModal(false)}
                    onSuccess={(results) => setImportResults(results)}
                    toggleRefresh={toggleRefresh}
                    setToggleRefresh={setToggleRefresh}
                    setShowActiveUsers={() => undefined}
                    uniqueIdentifier={customerSettings?.userUniqueIdentifier}
                    isCustomerSettingsLoading={isCustomerSettingsLoading}
                />
            )}

            {importResults && (
                <ImportResultsModal
                    open={!!importResults}
                    onClose={() => setImportResults(null)}
                    results={importResults}
                />
            )}

            {addUserModal && (
                <AddNewUsersModal
                    visible={addUserModal}
                    isEdit={false}
                    allTeams={allTeams || []}
                    onClose={() => setAddUserModal(false)}
                    toggleRefresh={toggleRefresh}
                    setToggleRefresh={setToggleRefresh}
                    setShowActiveUsers={() => undefined}
                    customerSettings={customerSettings!}
                    isCustomerSettingsLoading={isCustomerSettingsLoading}
                />
            )}

            <AreYouSureModal
                title="Are You Sure?"
                description="This will remove the user from the subscription."
                visible={modal.removeFromSubscription}
                loading={customerLoading.removeUserFromSubscription}
                onConfirm={async () => {
                    await CustomerService.removeUserFromSubscription(
                        selectedUser.id,
                        selectedUser.subscriptionId,
                    );
                    setSelectedUser({} as UserTypes.Model);
                    toggleModal('removeFromSubscription', false);
                    setToggleRefresh((v) => !v);
                }}
                onHide={() => {
                    setSelectedUser({} as UserTypes.Model);
                    toggleModal('removeFromSubscription', false);
                }}
            />

            <UserDetailsDrawer
                user={selectedUser}
                visible={modal.details}
                allTeams={allTeams || []}
                canRemove={canRemove}
                customerSettings={customerSettings ?? undefined}
                isCustomerSettingsLoading={isCustomerSettingsLoading}
                onSaved={() => setToggleRefresh((v) => !v)}
                onRemove={() => {
                    toggleModal('details', false);
                    toggleModal('removeFromSubscription', true);
                }}
                onClose={() => {
                    toggleModal('details', false);
                    setSelectedUser({} as UserTypes.Model);
                }}
            />
        </>
    );
};
